import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import {
  Canal,
  Topico,
  Comentario,
  CriarCanalDto,
  EditarCanalDto,
  CriarTopicoDto,
  EditarTopicoDto,
  CriarComentarioDto,
  EditarComentarioDto
} from '../types/comunicacao';
import comunicacaoService from '../services/comunicacaoService';
import { toast } from 'react-toastify';

interface ComunicacaoContextType {
  canais: Canal[];
  canalSelecionado: Canal | null;
  topicos: Topico[];
  topicoSelecionado: Topico | null;
  comentarios: Comentario[];
  loading: boolean;

  carregarCanais: () => Promise<void>;
  selecionarCanal: (canalId: string) => Promise<void>;
  criarCanal: (dados: CriarCanalDto) => Promise<void>;
  editarCanal: (id: string, dados: EditarCanalDto) => Promise<void>;
  removerCanal: (id: string) => Promise<void>;

  carregarTopicos: (canalId: string) => Promise<void>;
  selecionarTopico: (topicoId: string) => Promise<void>;
  criarTopico: (dados: CriarTopicoDto) => Promise<Topico>;
  editarTopico: (id: string, dados: EditarTopicoDto) => Promise<void>;
  removerTopico: (id: string) => Promise<void>;
  voltarParaCanal: () => void;

  carregarComentarios: (topicoId: string) => Promise<void>;
  criarComentario: (dados: CriarComentarioDto) => Promise<void>;
  editarComentario: (id: string, dados: EditarComentarioDto) => Promise<void>;
  removerComentario: (id: string) => Promise<void>;
}

const ComunicacaoContext = createContext<ComunicacaoContextType | undefined>(undefined);

export const useComunicacao = () => {
  const context = useContext(ComunicacaoContext);
  if (!context) {
    throw new Error('useComunicacao deve ser usado dentro de um ComunicacaoProvider');
  }
  return context;
};

interface ComunicacaoProviderProps {
  children: ReactNode;
}

export const ComunicacaoProvider: React.FC<ComunicacaoProviderProps> = ({ children }) => {
  const [canais, setCanais] = useState<Canal[]>([]);
  const [canalSelecionado, setCanalSelecionado] = useState<Canal | null>(null);
  const [topicos, setTopicos] = useState<Topico[]>([]);
  const [topicoSelecionado, setTopicoSelecionado] = useState<Topico | null>(null);
  const [comentarios, setComentarios] = useState<Comentario[]>([]);
  const [loading, setLoading] = useState(false);

  // ==================== CANAIS ====================

  const carregarCanais = useCallback(async () => {
    try {
      setLoading(true);
      const canaisData = await comunicacaoService.listarCanais();
      setCanais(canaisData);
    } catch (error: any) {
      console.error('Erro ao carregar canais:', error);
      toast.error(error.response?.data?.message || 'Erro ao carregar canais');
    } finally {
      setLoading(false);
    }
  }, []);

  const selecionarCanal = useCallback(async (canalId: string) => {
    try {
      setLoading(true);
      const canal = await comunicacaoService.obterCanal(canalId);
      setCanalSelecionado(canal);
      await carregarTopicos(canalId);
      setTopicoSelecionado(null);
      setComentarios([]);
    } catch (error: any) {
      console.error('Erro ao selecionar canal:', error);
      toast.error(error.response?.data?.message || 'Erro ao selecionar canal');
    } finally {
      setLoading(false);
    }
  }, []);

  const criarCanal = useCallback(async (dados: CriarCanalDto) => {
    try {
      setLoading(true);
      await comunicacaoService.criarCanal(dados);
      toast.success('Canal criado com sucesso!');
      await carregarCanais();
    } catch (error: any) {
      console.error('Erro ao criar canal:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar canal');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [carregarCanais]);

  const editarCanal = useCallback(async (id: string, dados: EditarCanalDto) => {
    try {
      setLoading(true);
      await comunicacaoService.editarCanal(id, dados);
      toast.success('Canal atualizado com sucesso!');
      await carregarCanais();
      if (canalSelecionado?.id === id) {
        const canalAtualizado = await comunicacaoService.obterCanal(id);
        setCanalSelecionado(canalAtualizado);
      }
    } catch (error: any) {
      console.error('Erro ao editar canal:', error);
      toast.error(error.response?.data?.message || 'Erro ao editar canal');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [carregarCanais, canalSelecionado]);

  const removerCanal = useCallback(async (id: string) => {
    try {
      setLoading(true);
      await comunicacaoService.removerCanal(id);
      toast.success('Canal removido com sucesso!');
      await carregarCanais();
      if (canalSelecionado?.id === id) {
        setCanalSelecionado(null);
        setTopicos([]);
      }
    } catch (error: any) {
      console.error('Erro ao remover canal:', error);
      toast.error(error.response?.data?.message || 'Erro ao remover canal');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [carregarCanais, canalSelecionado]);

  // ==================== TÓPICOS ====================

  const carregarTopicos = useCallback(async (canalId: string) => {
    try {
      setLoading(true);
      const topicosData = await comunicacaoService.listarTopicos(canalId);
      setTopicos(topicosData);
    } catch (error: any) {
      console.error('Erro ao carregar tópicos:', error);
      toast.error(error.response?.data?.message || 'Erro ao carregar tópicos');
    } finally {
      setLoading(false);
    }
  }, []);

  const selecionarTopico = useCallback(async (topicoId: string) => {
    try {
      setLoading(true);
      const topico = await comunicacaoService.obterTopico(topicoId);
      setTopicoSelecionado(topico);
      await carregarComentarios(topicoId);
    } catch (error: any) {
      console.error('Erro ao selecionar tópico:', error);
      toast.error(error.response?.data?.message || 'Erro ao selecionar tópico');
    } finally {
      setLoading(false);
    }
  }, []);

  const criarTopico = useCallback(async (dados: CriarTopicoDto): Promise<Topico> => {
    try {
      setLoading(true);
      const topico = await comunicacaoService.criarTopico(dados);
      toast.success('Tópico criado com sucesso!');
      await carregarTopicos(dados.canalId);
      return topico;
    } catch (error: any) {
      console.error('Erro ao criar tópico:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar tópico');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [carregarTopicos]);

  const editarTopico = useCallback(async (id: string, dados: EditarTopicoDto) => {
    try {
      setLoading(true);
      await comunicacaoService.editarTopico(id, dados);
      toast.success('Tópico atualizado com sucesso!');
      if (canalSelecionado) {
        await carregarTopicos(canalSelecionado.id);
      }
      if (topicoSelecionado?.id === id) {
        const topicoAtualizado = await comunicacaoService.obterTopico(id);
        setTopicoSelecionado(topicoAtualizado);
      }
    } catch (error: any) {
      console.error('Erro ao editar tópico:', error);
      toast.error(error.response?.data?.message || 'Erro ao editar tópico');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [canalSelecionado, topicoSelecionado, carregarTopicos]);

  const removerTopico = useCallback(async (id: string) => {
    try {
      setLoading(true);
      await comunicacaoService.removerTopico(id);
      toast.success('Tópico removido com sucesso!');
      if (canalSelecionado) {
        await carregarTopicos(canalSelecionado.id);
      }
      if (topicoSelecionado?.id === id) {
        setTopicoSelecionado(null);
        setComentarios([]);
      }
    } catch (error: any) {
      console.error('Erro ao remover tópico:', error);
      toast.error(error.response?.data?.message || 'Erro ao remover tópico');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [canalSelecionado, topicoSelecionado, carregarTopicos]);

  const voltarParaCanal = useCallback(() => {
    setTopicoSelecionado(null);
    setComentarios([]);
  }, []);

  // ==================== COMENTÁRIOS ====================

  const carregarComentarios = useCallback(async (topicoId: string) => {
    try {
      setLoading(true);
      const comentariosData = await comunicacaoService.listarComentarios(topicoId);
      setComentarios(comentariosData);
    } catch (error: any) {
      console.error('Erro ao carregar comentários:', error);
      toast.error(error.response?.data?.message || 'Erro ao carregar comentários');
    } finally {
      setLoading(false);
    }
  }, []);

  const criarComentario = useCallback(async (dados: CriarComentarioDto) => {
    try {
      await comunicacaoService.criarComentario(dados);
      await carregarComentarios(dados.topicoId);
      // Atualiza contagem de comentários no canal em background, sem bloquear a UI
      if (canalSelecionado) {
        carregarTopicos(canalSelecionado.id).catch(() => {});
      }
    } catch (error: any) {
      console.error('Erro ao criar comentário:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar comentário');
      throw error;
    }
  }, [carregarComentarios, canalSelecionado, carregarTopicos]);

  const editarComentario = useCallback(async (id: string, dados: EditarComentarioDto) => {
    try {
      await comunicacaoService.editarComentario(id, dados);
      toast.success('Comentário atualizado com sucesso!');
      // Atualiza localmente sem recarregar tudo
      setComentarios(prev => prev.map(c => c.id === id ? { ...c, ...dados } : c));
    } catch (error: any) {
      console.error('Erro ao editar comentário:', error);
      toast.error(error.response?.data?.message || 'Erro ao editar comentário');
      throw error;
    }
  }, []);

  const removerComentario = useCallback(async (id: string) => {
    try {
      await comunicacaoService.removerComentario(id);
      toast.success('Comentário removido com sucesso!');
      // Remove localmente sem recarregar tudo
      setComentarios(prev => prev.filter(c => c.id !== id));
      // Atualiza contagem no canal em background
      if (canalSelecionado) {
        carregarTopicos(canalSelecionado.id).catch(() => {});
      }
    } catch (error: any) {
      console.error('Erro ao remover comentário:', error);
      toast.error(error.response?.data?.message || 'Erro ao remover comentário');
      throw error;
    }
  }, [canalSelecionado, carregarTopicos]);

  const value: ComunicacaoContextType = {
    canais,
    canalSelecionado,
    topicos,
    topicoSelecionado,
    comentarios,
    loading,
    carregarCanais,
    selecionarCanal,
    criarCanal,
    editarCanal,
    removerCanal,
    carregarTopicos,
    selecionarTopico,
    criarTopico,
    editarTopico,
    removerTopico,
    voltarParaCanal,
    carregarComentarios,
    criarComentario,
    editarComentario,
    removerComentario
  };

  return (
    <ComunicacaoContext.Provider value={value}>
      {children}
    </ComunicacaoContext.Provider>
  );
};
